<template>
  <custom-menu-button />
  <div
    v-if="isLoading"
    class="custom-full-loading"
  />
  <div
    v-else
    class="body-scroll-container"
  >
    <div class="store-portal-image-container">
      <el-carousel
        v-if="computedWebsiteBanner.length > 0"
        :autoplay="false"
        height="320px"
      >
        <el-carousel-item
          v-for="(image, index) in computedWebsiteBanner"
          :key="`banner${index}`"
        >
          <img
            id="store-image"
            draggable="false"
            :src="image"
          >
        </el-carousel-item>
      </el-carousel>
      <div
        v-else
        class="image-default"
      >
        <img
          draggable="false"
          src="https://image.qlieer.app/icon/ic-beauty-logo-white-default.svg"
        >
      </div>
    </div>
    <div class="store-portal-container">
      <div class="store-header">
        <div class="store-title">
          <div>
            <div class="store-name">
              {{ storeInfo.websiteTitle }}
            </div>
          </div>
          <div class="store-button-container">
            <icon-button
              v-if="isShowConsultBtn"
              icon-type="message"
              @click.prevent="onClickMessage()"
            />
            <icon-button
              color-type="grey-7f"
              icon-type="info"
              @click.prevent="onClickInfo()"
            />
          </div>
        </div>
        <div v-if="computedDescription.length > 0">
          <!-- eslint-disable vue/no-v-html -->
          <div
            id="store-notes-block"
            class="store-notes"
            v-html="computedDescription"
          />
          <!-- eslint-enable -->
        </div>
      </div>
      <div
        v-if="isShowRetailEnable || isShowServiceEnable"
        class="store-appointment"
      >
        <el-button
          v-if="isShowRetailEnable"
          class="w-full"
          type="primary"
          @click.prevent="onClickStarted('retail')"
        >
          <span class="text-[16px] leading-[21px] font-600">購買商品</span>
        </el-button>
        <el-button
          v-if="isShowServiceEnable"
          class="w-full"
          type="primary"
          @click.prevent="onClickStarted('appointment')"
        >
          <span class="text-[16px] leading-[21px] font-600">預約服務</span>
        </el-button>
      </div>
      <div class="store-info">
        <div
          v-for="section in storeInfoSection"
          :key="section.id"
        >
          <div class="section-title">
            {{ section.title }}
          </div>
          <div
            v-for="col in section.cols"
            :key="col.id"
          >
            <div class="col-title">
              {{ col.title }}
            </div>
            <!-- 營業時間 -->
            <div
              v-if="col.id === 'openingHours'"
              class="business-hours"
              :class="[!isShowHourDetail ? 'hidden b-b-1-white-f5' : '']"
              @click.prevent="isShowHourDetail = !isShowHourDetail"
            >
              <div class="business-hours-content">
                <div>
                  <div class="sub-content">
                    {{ computedNowOpeningHours }}
                  </div>
                </div>
                <icon-button
                  class="white-button"
                  color-type="blue"
                  :icon-type="!isShowHourDetail ? 'arrow-down' : 'arrow-up'"
                  type="primary"
                />
              </div>
              <div class="all-day">
                <div
                  v-for="(weekTimes, weekKey) in computedRealOpenHour.open"
                  :key="`weekday-${weekKey}`"
                  class="weekday-section"
                  :class="[weekKey === todayWeekdayKey ? 'active' : '']"
                >
                  <div class="weekday-text">
                    {{ renderWeekdayText(weekKey) }}
                  </div>
                  <div v-if="weekTimes.length > 0">
                    <div
                      v-for="(time, timeIndex) in weekTimes"
                      :key="`${weekKey}-${timeIndex}`"
                      class="weekday-hour"
                    >
                      {{ `${time.start} ~ ${time.end}` }}
                    </div>
                  </div>
                  <div
                    v-else
                    class="weekday-hour"
                  >
                    公休
                  </div>
                </div>
              </div>
            </div>
            <!-- 電話 || 地址 -->
            <div
              v-else-if="col.id === 'phone' || col.id === 'address'"
              class="col-description"
            >
              <a
                class="section"
                :href="col.id === 'phone' ? computedPhone : computedMapLocation"
                target="_blank"
              >
                <div class="link-text">
                  {{ storeInfo[col.id] }}
                </div>
              </a>
            </div>
            <div
              v-else
              class="col-description"
            >
              {{ col.formatter ? col.formatter(storeInfo[col.id]) : storeInfo[col.id] }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <template v-if="onceIsStoreInfoActive">
    <lazy-popup-store-info
      v-model="isStoreInfoActive"
      :store="storeInfo"
    />
  </template>
</template>

<script setup>
import DOMPurify from 'dompurify'
import { get } from 'lodash-es'

import { useStoreCname } from '@/composables/useStoreCname'
import {
  convertTextWithLink,
  formatWeekdayNames,
  sendMixpanelEvent,
} from '@/utils'
import { getNow } from '@/utils/timeFormat'

const LazyPopupStoreInfo = defineAsyncComponent(
  () => import('@/components/popup/PopupStoreInfo.vue'),
)

const isStoreInfoActive = ref(false)
const onceIsStoreInfoActive = ref(false)

const store = useStore()
const router = useRouter()
const route = useRoute()
const queryObj = computed(() => route.query)
const storeCname = useStoreCname()
const designerId = computed(() => get(queryObj.value, 'userId', null))
const storeInfo = computed(() => store.getters['store/storeSetting'])

const realOpenHour = ref([])
const isLoading = ref(true)
const storeInfoSection = [
  {
    id: 'firstSection',
    title: '基本資訊',
    class: [],
    cols: [
      { id: 'name', title: '店名', class: [] },
      { id: 'address', title: '地址', class: [] },
      { id: 'phone', title: '電話', class: [] },
      { id: 'openingHours', title: '排班時間', class: [] },
    ],
  },
]
const luxonWeekday = getNow({ type: 'DateTimeObject' }).weekday
const todayWeekdayNumber = luxonWeekday === 7 ? 0 : luxonWeekday
const computedDescription = computed(() =>
  convertTextWithLink(
    DOMPurify.sanitize(get(storeInfo.value, 'websiteDescription', '')),
  ),
)
const computedRealOpenHour = computed(() => {
  // NOTE: api 資料回來後，在 html render 上有可能沒有按照順序回傳
  const weekdayKeys = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ]
  const obj = { open: {} }
  weekdayKeys.forEach(key => {
    Object.assign(obj.open, { [key]: realOpenHour.value.open[key] })
  })
  return obj
})
const computedNowOpeningHours = computed(() => {
  const openKey = Object.keys(computedRealOpenHour.value.open)[
    todayWeekdayNumber
  ]
  const hours = computedRealOpenHour.value.open[openKey]
  let lastHour = ''
  if (hours.length > 0) {
    lastHour = hours[hours.length >= 1 ? hours.length - 1 : 0].end
  }
  return hours.length > 0 ? `今日營業至 ${lastHour}` : '公休'
})
const computedPhone = computed(() => `tel:${storeInfo.value.phone}`)
const computedMapLocation = computed(
  () => `http://maps.google.com/?q=${storeInfo.value.address}`,
)
const computedWebsiteBanner = computed(() =>
  get(storeInfo.value, 'websiteBanner', []),
)
const isShowRetailEnable = computed(() =>
  get(storeInfo.value, 'retailEnable', false),
)
const isShowServiceEnable = computed(() =>
  get(storeInfo.value, 'serviceEnable', false),
)
const isShowConsultBtn = computed(() =>
  get(storeInfo.value, 'consultEnable', false),
)
const isShowHourDetail = ref(false)
const todayWeekdayKey = computed(() => {
  return Object.keys(computedRealOpenHour.value.open)[todayWeekdayNumber]
})

const renderWeekdayText = weekdayKey => {
  const weekIndex = Object.keys(computedRealOpenHour.value.open).indexOf(
    weekdayKey,
  )
  return formatWeekdayNames(weekIndex)
}
const initData = async () => {
  const obj = designerId.value ? { userId: designerId.value } : {}
  const result = await store.dispatch(
    'store/getStore',
    Object.assign({ code: storeCname.value }, obj),
  )
  if (!result || result?.status === 'RESOURCE_NOT_FOUND') {
    router.push({ name: 'NotFound', query: queryObj.value })
  }
  realOpenHour.value = designerId.value
    ? storeInfo.value.workingHours
    : storeInfo.value.openingHours
  isLoading.value = false
}
const onClickInfo = () => {
  isStoreInfoActive.value = true

  if (onceIsStoreInfoActive.value) return
  onceIsStoreInfoActive.value = true
}

const onClickMessage = () => {
  const consultURL = get(storeInfo.value, 'consultURL', '')
  const element = document.createElement('a')
  element.href = consultURL
  element.target = '_blank'
  element.click()
}
const onClickStarted = type => {
  router.push({
    name: type === 'retail' ? 'StoreRetail' : 'StoreMenu',
    query: queryObj.value,
  })
}
onMounted(() => {
  sendMixpanelEvent({
    title: '線上預約_進入線上網站首頁',
    data: { type: designerId.value ? 'user' : 'store' },
  })
  initData()
})
</script>
